/**
 * src/config/products.js
 *
 * Single source of truth for all VISINVOKE products.
 * To add a new product: add one entry here and register its backend blueprint.
 * Nothing else needs changing in the shell.
 *
 * Category colour identity (product.color / product.accentGradient / product.palette)
 * is applied from the authoritative bright category system in ./categoryTheme.js
 * at the bottom of this file, so the Dashboard always matches the DCC.
 */

import { CATEGORY_PALETTES } from './categoryTheme';

export const PRODUCTS = {
  visual: {
    key:               'visual',
    displayName:       'D&D Image Generators',
    navTitle:          'D&D Image',
    shortName:         'D&D',
    route:             '/dashboard/visual',
    browseRoute:       '/generators',
    color:             '#8b5cf6',
    accentGradient:    'linear-gradient(135deg, #8b5cf6, #a855f7)',
    icon:              '🐉',
    navDesc:           'Fantasy, RPG and game-based image generators',
    searchEndpoint:    '/v1/search/autocomplete',
    searchPlaceholder: 'Search generators, families, sections...',
    engineBase:        '/engine/visinvoke-engine.html',
    engineParam:       'slug',
    usageLabel:        'Credits',
    usageLabelShort:   'credits',
    dashboardCardTitle:'D&D Image Generator Library',
    cardDesc:          'Browse all live D&D visual generators. Each generator includes up to 4,000+ structured selections and up to 38 custom-configured inputs across 6 configuration sections, enabling precise, highly controlled, and repeatable image generation.',
    pillsLive:         ['🐉 D&D Generators', '🔍 Full Search', '⚡ A-Z Filter', '4,000+ Structured Selections + 38 Custom Inputs'],
    pillsSoon:         [],
    browseButtonLabel: 'Browse Generators →',
    planLabel:         'Generator Plan',
    quickLinks: [
      { icon: '📋', title: 'Manage Subscription', sub: 'Change plan, billing history',                                    route: '/manage'      },
      { icon: '⭐', title: 'View Plans',           sub: 'View all plans and pricing',                                      route: '/upgrade'     },
      { icon: '🐉', title: 'Browse Generators',   sub: 'Browse all D&D and professional generators',                      route: '/generators'  },
      { icon: '🛠️', title: 'Build Workspace',     sub: 'Open your saved builds, presets, templates, and build history',  route: '/workspace'   },
    ],
    comingSoon: false,
    launchHandler: (result) => {
      if (result.slug) {
        window.location.href = `/engine/visinvoke-engine.html?slug=${result.slug}`;
      }
    },
  },

  professional: {
    key:               'professional',
    displayName:       'Professional Generators',
    navTitle:          'Professional',
    shortName:         'Pro',
    route:             '/dashboard/professional',
    browseRoute:       '/generators?library=professional',
    color:             '#0ea5e9',
    accentGradient:    'linear-gradient(135deg, #0ea5e9, #6366f1)',
    icon:              '💼',
    navDesc:           'Business, marketing and commercial generators',
    searchEndpoint:    '/v1/search/autocomplete',
    searchPlaceholder: 'Search professional generators...',
    // Professional launches through EngineWrapper (`/engine`), which reads the
    // generator's own `engine` value and loads the matching per-engine file -
    // one of the six 92-control Professional engines. It must never be pinned to
    // a single engine file here: doing so is what pointed the whole family at the
    // superseded 38-control `visinvoke-engine.html`.
    engineBase:        '/engine',
    engineParam:       'slug',
    usageLabel:        'Credits',
    usageLabelShort:   'credits',
    dashboardCardTitle:'Professional Generator Library',
    cardDesc:          'Browse 24 professional category libraries. Architecture, branding, product photography, film, fashion, and more — each with structured configuration options for precise commercial image specifications.',
    pillsLive:         ['🏛️ Architecture', '📢 Branding & Marketing', '📸 Product Photography', '4,000+ Generators'],
    pillsSoon:         [],
    browseButtonLabel: 'Browse Professional Generators →',
    planLabel:         'Generator Plan',
    quickLinks: [
      { icon: '📋', title: 'Manage Subscription', sub: 'Change plan, billing history',                                    route: '/manage'      },
      { icon: '⭐', title: 'View Plans',           sub: 'View all plans and pricing',                                      route: '/upgrade'     },
      { icon: '💼', title: 'Browse Professional', sub: '24 professional generator categories',                            route: '/generators?library=professional' },
      { icon: '🛠️', title: 'Build Workspace',     sub: 'Open your saved builds, presets, templates, and build history',  route: '/workspace'   },
    ],
    comingSoon: false,
    // Superseded. Every live Professional launch goes through
    // `navigate('/engine', { state: { slug } })` -> EngineWrapper, which resolves
    // the correct per-engine file from the generator's own `engine` value. This
    // hook is not called anywhere; it previously hard-coded the superseded
    // 38-control `visinvoke-engine.html` for all six Professional categories,
    // which would have bypassed engine resolution entirely if it were ever wired
    // up. Left as a no-op rather than deleted so the product shape stays uniform.
    launchHandler:     () => {},
  },

  battlemaps: {
    key:               'battlemaps',
    displayName:       'Battlemaps',
    shortName:         'Battlemaps',
    route:             '/dashboard/battlemaps',
    browseRoute:       '/battlemaps',
    color:             '#D4AF37',
    accentGradient:    'linear-gradient(135deg, #D4AF37, #b8962e)',
    icon:              '🗺️',
    navDesc:           'Tactical maps, assets, and environment generation',
    searchEndpoint:    '/v1/battlemaps/search/autocomplete',
    searchPlaceholder: 'Search battlemaps, environments, packs...',
    engineBase:        '/engine/battlemaps-engine.html',
    engineParam:       'pack',
    usageLabel:        'Credits',
    usageLabelShort:   'credits',
    dashboardCardTitle:'Battlemap Library',
    cardDesc:          'Browse the Battlemaps library. Each generator pack includes curated tactical environments, encounter scenes, and modular map assets for your campaign.',
    pillsLive:         [],
    pillsSoon:         ['🗺️ Dungeon Maps', '🌲 Wilderness Encounters', '🏰 Castle & Stronghold', '🌊 Coastal & Water'],
    browseButtonLabel: 'Browse Battlemaps →',
    planLabel:         'Battlemaps Plan',
    quickLinks: [
      { icon: '📋', title: 'Manage Subscription', sub: 'Change plan, billing history',                                    route: '/manage'               },
      { icon: '⭐', title: 'View Plans',            sub: 'Battlemaps plans and pricing',                                   route: '/battlemaps/pricing'   },
      { icon: '🗺️', title: 'Browse Battlemaps',   sub: 'Tactical maps and encounter scenes',                             route: '/battlemaps/engines'   },
      { icon: '🛠️', title: 'Build Workspace',     sub: 'Open your saved builds, presets, templates, and build history',  route: '/workspace'            },
    ],
    comingSoon: false,
    launchHandler: (result) => {
      if (result.slug) {
        window.location.href = `/engine/battlemaps-engine.html?pack=${result.slug}`;
      }
    },
  },

  battletracker: {
    key:               'battletracker',
    displayName:       'Combat PRO',
    shortName:         'Combat PRO',
    route:             '/dashboard/battletracker',
    browseRoute:       '/battletracker',
    color:             '#22c55e',
    accentGradient:    'linear-gradient(135deg, #22c55e, #0f9f57)',
    icon:              '⚔️',
    navDesc:           'Complete D&D combat and encounter management system',
    // Category-tile subtitle, two fixed lines (owner copy 2026-10-06).
    navDescLines:      ['Complete D&D combat and', 'encounter management system'],
    searchEndpoint:    '/v1/battletracker/search/autocomplete',
    searchPlaceholder: 'Search encounters, monsters, campaigns...',
    engineBase:        '/battletracker/battletracker.html',
    engineParam:       'id',
    usageLabel:        'Tracker Sessions',
    usageLabelShort:   'sessions',
    dashboardCardTitle:'Combat PRO',
    cardDesc:          'Manage your D&D combat sessions. Track initiative, HP, conditions, spell durations and more across your campaign encounters.',
    pillsLive:         ['⚔️ Initiative Tracker', '❤️ HP & Conditions', '🎲 Encounter Builder', '👹 Monster Library'],
    pillsSoon:         [],
    browseButtonLabel: 'Run Combat PRO →',
    planLabel:         'Combat PRO Plan',
    quickLinks: [
      { icon: '📋', title: 'Manage Account',        sub: 'Subscription and billing',                                      route: '/manage'       },
      { icon: '⭐', title: 'View Plans',             sub: 'Combat PRO plans and pricing',                           route: '/upgrade'      },
      { icon: '⚔️', title: 'Open Combat PRO',   sub: 'Launch Combat PRO',                                     route: '/battletracker'},
      { icon: '🛠️', title: 'Build Workspace',      sub: 'Open your saved builds, presets, templates, and build history', route: '/workspace'    },
    ],
    comingSoon: false,
    launchHandler: (result) => {
      window.location.href = '/battletracker/battletracker.html';
    },
  },

  gaming: {
    key:               'gaming',
    displayName:       'Gaming',
    navTitle:          'Gaming',
    shortName:         'Gaming',
    route:             '/dashboard/gaming',
    browseRoute:       '/dashboard/gaming',
    color:             '#E53B63',
    accentGradient:    'linear-gradient(135deg, #9f1239, #be123c)',
    icon:              '\u{1F3AE}',
    navDesc:           'RPG Worlds and Online Gaming \u2014 Virtual Shop',
    searchEndpoint:    null,
    searchPlaceholder: '',
    engineBase:        null,
    engineParam:       null,
    usageLabel:        'Destinations',
    usageLabelShort:   'destinations',
    dashboardCardTitle:'Gaming',
    cardDesc:          'Two ways to play: enter the RPG Worlds system, or step into Online Gaming and start building your Virtual Shop collection.',
    pillsLive:         [],
    pillsSoon:         [],
    browseButtonLabel: 'Enter Gaming \u2192',
    planLabel:         'Gaming',
    quickLinks:        [],
    comingSoon:        false,
    // Gaming is an umbrella category. Its two destinations (the existing RPG
    // Worlds system and the new Online Gaming / Virtual Shop experience) keep the
    // Gaming panel highlighted while the customer is inside them.
    childRoutes:       ['/dashboard/rpg', '/dashboard/virtual-shop', '/dashboard/product/virtual-shop'],
    launchHandler:     () => {},
  },

  rpg: {
    key:               'rpg',
    displayName:       'RPG Text Game',
    navTitle:          'RPG Worlds',
    shortName:         'RPG',
    route:             '/dashboard/rpg',
    browseRoute:       '/dashboard/rpg',
    color:             '#be123c',
    accentGradient:    'linear-gradient(135deg, #9f1239, #be123c)',
    icon:              '📖',
    navDesc:           'Persistent AI worlds with endless adventures',
    searchEndpoint:    null,
    searchPlaceholder: '',
    engineBase:        null,
    engineParam:       null,
    usageLabel:        'Sessions',
    usageLabelShort:   'sessions',
    dashboardCardTitle:'RPG Text Game',
    cardDesc:          'A persistent solo AI-powered RPG experience. Coming Late 2026.',
    pillsLive:         [],
    pillsSoon:         ['📖 Persistent World', '🏰 Faction Systems', '⚔️ Solo Party Play', 'Coming Late 2026'],
    browseButtonLabel: 'Learn More →',
    planLabel:         'RPG Plan',
    quickLinks:        [],
    comingSoon:        true,
    comingSoonLabel:   'Coming Late 2026',
    launchHandler:     () => {},
  },

  security: {
    key:               'security',
    displayName:       'Security',
    shortName:         'Security',
    route:             '/dashboard/security',
    browseRoute:       '/dashboard/security',
    color:             '#d97706',
    accentGradient:    'linear-gradient(135deg, #b45309, #f59e0b)',
    icon:              '🛡️',
    navDesc:           'Professional protection for every computer',
    searchEndpoint:    null,
    searchPlaceholder: '',
    engineBase:        null,
    engineParam:       null,
    usageLabel:        'Services',
    usageLabelShort:   'services',
    dashboardCardTitle:'Security',
    cardDesc:          'Professionally managed security services designed for home users, professionals and businesses. Managed PC Protection and Track, Lock & Wipe.',
    pillsLive:         ['🛡️ Managed PC Protection', '🔒 Track, Lock & Wipe', '🧑\u200d🔧 Continuously Monitored', 'Email Support Included'],
    pillsSoon:         [],
    browseButtonLabel: 'View Security →',
    planLabel:         'Security Plan',
    quickLinks:        [],
    comingSoon:        false,
    launchHandler:     () => {},
  },

  utilities: {
    key:               'utilities',
    displayName:       'Utilities',
    shortName:         'Utilities',
    route:             '/dashboard/utilities',
    browseRoute:       '/dashboard/utilities',
    color:             '#475569',
    accentGradient:    'linear-gradient(135deg, #334155, #475569)',
    icon:              '🔧',
    navDesc:           'Business, Windows and IT solutions',
    searchEndpoint:    null,
    searchPlaceholder: '',
    engineBase:        null,
    engineParam:       null,
    usageLabel:        'Tools',
    usageLabelShort:   'tools',
    dashboardCardTitle:'Utilities',
    cardDesc:          'Administration and service management tools for the VISINVOKE ecosystem.',
    pillsLive:         [],
    pillsSoon:         ['📅 Content Scheduling', '⚙️ Workflow Planner', '📊 Business Toolkit', 'Coming Early 2027'],
    browseButtonLabel: 'Learn More →',
    planLabel:         'Utilities Plan',
    quickLinks:        [],
    comingSoon:        true,
    comingSoonLabel:   'Coming Early 2027',
    launchHandler:     () => {},
  },
};

// ── Authoritative category colour application ──────────────────────────────
// Apply the exact bright category palette to every product. The category
// PRIMARY drives product.color; the category CTA gradient drives
// product.accentGradient; the full palette is exposed as product.palette for
// pages that need surfaces, borders, glow, atmosphere, text accent, etc.
// This guarantees a single source of truth and keeps the seven families
// aligned with the DCC — no purple fallback for non-D&D families.
Object.keys(PRODUCTS).forEach((key) => {
  const pal = CATEGORY_PALETTES[key];
  if (pal) {
    PRODUCTS[key].color = pal.primary;
    PRODUCTS[key].accentGradient = pal.cta;
    PRODUCTS[key].palette = pal;
  }
});

// Ordered list for nav display
export const PRODUCT_ORDER = ['visual', 'professional', 'battlemaps', 'battletracker', 'gaming', 'security', 'utilities'];

// localStorage key for last-used product
export const LAST_PRODUCT_KEY = 'visinvoke_last_product';

export function getProduct(key) {
  return PRODUCTS[key] || PRODUCTS.visual;
}

export function getProductFromRoute(pathname) {
  for (const key of PRODUCT_ORDER) {
    const p = PRODUCTS[key];
    if (pathname.startsWith(p.route)) return p;
    if (p.childRoutes && p.childRoutes.some(r => pathname.startsWith(r))) return p;
  }
  return null;
}
